import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { transportDistributionService } from "@/services/transport-distribution/transportDistributionService";

interface ConfirmAction {
  type: "archive" | "restore";
  sourceId: string;
  sourceName: string;
}

export const useConfirmationDialog = (onSuccess: () => void) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();

  const openDialog = (action: ConfirmAction) => {
    setConfirmAction(action);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setConfirmAction(null);
  };

  const handleConfirm = async () => {
    if (!confirmAction || !user) return;

    setIsLoading(true);
    try {
      if (confirmAction.type === "archive") {
        await transportDistributionService.archive(confirmAction.sourceId, user.uid);
        toast({
          title: t("transportDistribution.archiveSuccess"),
          description: t("transportDistribution.archiveSuccessDescription", { name: confirmAction.sourceName }),
        });
      } else {
        await transportDistributionService.restore(confirmAction.sourceId, user.uid);
        toast({
          title: t("transportDistribution.restoreSuccess"),
          description: t("transportDistribution.restoreSuccessDescription", { name: confirmAction.sourceName }),
        });
      }
      onSuccess();
      closeDialog();
    } catch (error) {
      toast({
        title: t("transportDistribution.error"),
        description: t("transportDistribution.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isOpen,
    confirmAction,
    isLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  };
}; 